import type { TimelineLane } from './components/timeline'
import type { Player } from './use-player'

/**
 * Retained video, reduced to the one question the screen actually asks:
 * **is there a picture to play, and if not, why not?**
 *
 * A pure function with no React in it, for the same reason
 * `features/call/recording-control.ts` is one: every interesting case here is a
 * combination of upstream fields that is awkward to produce on demand, and the
 * only way to have confidence in the awkward ones is to write them down as data
 * and assert on them. See `video.test.ts`.
 *
 * ===========================================================================
 * WHAT IS TRUSTED, AND WHAT IS DELIBERATELY NOT
 *
 * `render` is trusted. `policy.actual` and `policy.transition` are NOT, and
 * that is not caution for its own sake — it is measured. On 2026-08-09
 * `s_loadvideo_20260809043251_001`, a call that ended four hours earlier with a
 * verified 49-second MP4 in storage, still reported:
 *
 *     policy.actual      "none"
 *     policy.transition  "enabling"
 *     applied_revision   1        revision 1     (so the CAS fence says settled)
 *
 * A screen that decided "is there video" from `actual` would have shown that
 * session as having none, forever, with the file sitting right there. This is
 * the video channel's copy of the flaw FINDINGS.md #42(a) reports on the audio
 * one, now observed rather than assumed.
 *
 * So the rule is: `render` decides whether there is a picture. The policy is
 * read ONLY for `ever_enabled` — a monotonic history fact, not a live claim —
 * and for the failure text that explains an absent render.
 *
 * And within `render`, the field that decides PLAYABILITY is `stream_path`, not
 * merely the lifecycle value. The contract now enumerates the states and says
 * that `uploaded` still awaits independent verification; `stream_path` is the
 * authenticated, ready-only playback capability.
 * ===========================================================================
 */

/* -------------------------------------------------------------------------- */
/* the wire shape                                                              */
/* -------------------------------------------------------------------------- */

/*
 * Mirrors what `apps/bff/src/routes/sessions.ts` returns. It is written out
 * here rather than imported because the browser bundle must not reach into the
 * server package, and `@era/shared` does not yet re-export the generated video
 * types. Both sides derive from the same generated `SessionDetail`, so the
 * shapes agree by construction; the moment `@era/shared/era.ts` names
 * `VideoPipelineStatus`, both should switch to it.
 */

export interface VideoRenderStatus {
  render_id: string
  revision: number
  state: 'planned' | 'rendering' | 'uploaded' | 'ready' | 'failed' | 'deleted'
  key: string
  content_type: string
  size_bytes?: number | null
  duration_ms?: number | null
  /** Wall-clock instant represented by composite t=0. */
  timeline_start?: string | null
  /** False means the valid file is black-filled throughout. */
  has_video_content?: boolean | null
  width?: number | null
  height?: number | null
  started_at?: string | null
  verified_at?: string | null
  /** Contract-guaranteed: present ONLY for a ready, verified MP4. */
  stream_path?: string | null
  error?: string | null
}

export interface VideoProgramStatus {
  program_id: string
  mode: string
  state: string
  focus_identity?: string | null
  error?: string | null
}

export interface VideoSegmentsSummary {
  count: number
  verified: number
  failed: number
  first_frame_at: string | null
  lead_black_ms: number | null
}

export interface VideoPipelineView {
  policy: {
    desired: { mode: string; focus_identity?: string | null }
    /** Not trusted. See the header. */
    actual: string
    transition: string
    error?: string | null
    ever_enabled: boolean
  }
  programs: VideoProgramStatus[]
  render: VideoRenderStatus | null
  segments: VideoSegmentsSummary
  jobs: { kind?: string; state?: string }[]
}

export interface SessionVideoStatus {
  session_id: string
  video_pipeline: VideoPipelineView | null
  timeline_start: string | null
  timeline_duration_ms: number | null
  upstream_unavailable: boolean
  fetched_at: string
}

/* -------------------------------------------------------------------------- */
/* clock translation                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Milliseconds from the document/audio origin to MP4 t=0.
 *
 * Selected video deliberately starts at the first video-enabled policy
 * boundary, while transcript/activity offsets remain relative to the audio
 * timeline. Wall-clock origins make that difference exact:
 *
 *     video_ms = document_ms - offset_ms
 */
export function mediaTimelineOffsetMs(
  mediaTimelineStart: string | null | undefined,
  documentTimelineStart: string | null | undefined,
): number {
  if (!mediaTimelineStart || !documentTimelineStart) return 0
  const media = Date.parse(mediaTimelineStart)
  const document = Date.parse(documentTimelineStart)
  return Number.isFinite(media) && Number.isFinite(document) ? media - document : 0
}

/**
 * Present one underlying media clock in document/transcript coordinates.
 *
 * The media element and player card remain honestly MP4-relative. Transcript
 * highlighting and turn clicks receive this view so a document timestamp is
 * translated exactly once at their boundary. A transcript turn before video
 * was enabled clamps to MP4 t=0 because no retained video exists for it.
 */
export function playerOnDocumentTimeline(player: Player, offsetMs: number): Player {
  if (!Number.isFinite(offsetMs) || offsetMs === 0) return player
  return {
    ...player,
    getTimeMs: () => player.getTimeMs() + offsetMs,
    durationMs: Math.max(0, player.durationMs + offsetMs),
    seekMs: (documentMs, options) =>
      player.seekMs(Math.max(0, documentMs - offsetMs), options),
  }
}

/**
 * Put session-timebase speech segments on the MP4's own clock and clip the
 * portions for which the MP4 has no bytes. Speaker lanes are retained even
 * when all of that speaker's speech falls outside the video window so roster
 * order and colour never change merely because video started late.
 */
export function lanesOnMediaTimeline(
  lanes: TimelineLane[],
  offsetMs: number,
  mediaDurationMs: number | null,
): TimelineLane[] {
  const upper =
    typeof mediaDurationMs === 'number' && Number.isFinite(mediaDurationMs) && mediaDurationMs > 0
      ? mediaDurationMs
      : Number.POSITIVE_INFINITY

  return lanes.map((lane) => ({
    ...lane,
    segments: lane.segments.flatMap((segment) => {
      const start_ms = Math.max(0, segment.start_ms - offsetMs)
      const end_ms = Math.min(upper, segment.end_ms - offsetMs)
      return end_ms > start_ms ? [{ start_ms, end_ms }] : []
    }),
  }))
}

/* -------------------------------------------------------------------------- */
/* the state                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Five outcomes, and four of them are not "here is your video".
 *
 * They are separated because they need DIFFERENT SENTENCES and different
 * behaviour, not because the code is tidier that way:
 *
 *   - `absent` never changes. Nothing polls, and the UI must not imply waiting.
 *   - `pending` changes on its own; `composite_ready` invalidates the active
 *     video query, with a slow poll retained only as missed-event recovery.
 *   - `failed` is the only one that is a fault. It must not look like `pending`
 *     or a reader waits forever for a file that will never exist.
 *   - `ready` may still carry a `caveat`: a failed capture program, or a long
 *     black lead-in. Both produce a video that plays and looks wrong.
 *   - `unavailable` is OUR side being unable to ask. Distinct from all of the
 *     above because the video may well be fine.
 */
export type VideoState =
  | { kind: 'absent'; title: string; detail: string }
  | { kind: 'pending'; title: string; detail: string }
  | { kind: 'failed'; title: string; detail: string }
  | { kind: 'unavailable'; title: string; detail: string }
  | {
      kind: 'ready'
      render: VideoRenderStatus
      /** Aspect ratio for the surface, so metadata arriving causes no reflow. */
      aspectRatio: number
      durationMs: number | null
      /** Non-null when the video plays but a viewer would think it is broken. */
      caveat: string | null
    }

/** 16:9. Every render is 720p today (`VideoResolution` has one member). */
const DEFAULT_ASPECT = 16 / 9

/** Below this, a black lead-in is a startup artefact and not worth a sentence. */
const LEAD_BLACK_NOTICE_MS = 2_000

export function deriveVideoState(status: SessionVideoStatus | null): VideoState {
  if (status === null) {
    return {
      kind: 'unavailable',
      title: 'Checking for video',
      detail: 'Asking the control plane whether this call retained any video.',
    }
  }

  if (status.upstream_unavailable || status.video_pipeline === null) {
    return {
      kind: 'unavailable',
      title: 'Cannot tell whether this call has video',
      detail:
        'The control plane could not be reached. A completion notification may already be stored, but current playback status and bytes still require the control plane.',
    }
  }

  const pipeline = status.video_pipeline
  const render = pipeline.render

  /* -- it plays ----------------------------------------------------------- */
  //
  // `stream_path` first, for the reason in the header. `state === 'ready'` is
  // accepted as a fallback only so that a deployment which stops sending
  // `stream_path` degrades to playing rather than to claiming there is nothing.
  if (render && (render.stream_path || render.state === 'ready')) {
    return {
      kind: 'ready',
      render,
      aspectRatio: aspectRatioOf(render),
      durationMs: render.duration_ms ?? null,
      caveat: describeCaveat(pipeline),
    }
  }

  /* -- it failed ----------------------------------------------------------- */
  if (render && isFailedState(render.state)) {
    return {
      kind: 'failed',
      title: 'The video could not be assembled',
      detail: [
        `The recorder captured ${describeSegments(pipeline.segments)}, but composing the final file failed`,
        render.error ? ` (${render.error})` : '',
        '. This is a fault, not a wait — the file will not appear on its own.',
      ].join(''),
    }
  }

  /* -- it is coming -------------------------------------------------------- */
  //
  // Checked BEFORE `ever_enabled`, because a render row exists only once the
  // pipeline has something to render: its presence outranks a policy that has
  // already been torn back down to none.
  if (render) {
    return {
      kind: 'pending',
      title: 'The video is still being assembled',
      detail: `The call is over and the recorder is composing the final file from ${describeSegments(
        pipeline.segments,
      )}. This page rechecks by itself.`,
    }
  }

  /* -- it was never asked for ---------------------------------------------- */
  if (!pipeline.policy.ever_enabled) {
    return {
      kind: 'absent',
      title: 'No video was kept for this call',
      detail:
        'The unified recording policy never enabled its retained-video child here. The call may well have been a normal live video call — only retained audio was requested.',
    }
  }

  /* -- enabled, but nothing came back -------------------------------------- */
  //
  // The genuinely ambiguous case, and it is split by whether a capture program
  // ever ran. A failed program with no render is a fault; no program at all,
  // on a call that is still going, is just early.
  const failedProgram = pipeline.programs.find(
    (program) => isFailedState(program.state) && hasText(program.error),
  )
  if (failedProgram) {
    return {
      kind: 'failed',
      title: 'Video was enabled but never recorded',
      detail: `The recorder was asked for ${failedProgram.mode} and could not start: ${failedProgram.error}. Nothing was captured, so there will be no file.`,
    }
  }

  if (hasText(pipeline.policy.error)) {
    return {
      kind: 'failed',
      title: 'Video was enabled but never recorded',
      detail: `The retained-video policy is in a failed state: ${pipeline.policy.error}. Nothing was captured, so there will be no file.`,
    }
  }

  return {
    kind: 'pending',
    title: 'Video was enabled; nothing has arrived yet',
    detail:
      'A retained-video window was opened for this call but no composite exists yet. If the call is still running this is expected — the file is made after it ends.',
  }
}

/* -------------------------------------------------------------------------- */
/* prose                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The sentence a viewer needs when the video plays but looks wrong.
 *
 * Both cases here produce a file that opens, seeks and reports a duration —
 * and shows the viewer something they will read as a bug unless it is named.
 */
function describeCaveat(pipeline: VideoPipelineView): string | null {
  /*
   * AN ENTIRELY BLACK FILE, which is the worst one to leave unexplained.
   *
   * Zero segments means no camera track was ever captured — and the recorder
   * still produces a complete, valid, correctly-timed MP4 for the requested
   * video window, because the composite pads what it does not have. Measured on
   * a legacy probe session created for this
   * work, 2026-08-09: 67.006 s, 1280x720, H.264 + AAC, 174 kB, and mean frame
   * luminance 0.0 at t=1 s, 30 s and 60 s. It plays perfectly and shows nothing.
   *
   * Checked FIRST, before the lead-in, because "the first 19 seconds are black"
   * is actively misleading about a file that is black all the way through.
   */
  if (pipeline.render?.has_video_content === false || pipeline.segments.count === 0) {
    const reason = pipeline.programs.find(
      (program) => isFailedState(program.state) && hasText(program.error),
    )?.error
    return reason
      ? `No camera was ever recorded — the capture program failed (${reason}) — so this file plays as black for its whole length.`
      : 'No camera track was captured for this call, so this file plays as black for its whole length. The audio is real.'
  }

  const lead = pipeline.segments.lead_black_ms
  if (lead !== null && lead >= LEAD_BLACK_NOTICE_MS) {
    // Measured, not theoretical: `s_loadvideo_20260809043251_001` opens on
    // 18.7 seconds of black on a legacy render because no selected frames
    // arrived until well after its render origin. New v2 renders trim the
    // audio-only head, but capture startup can still leave a smaller honest
    // gap after video was enabled.
    return `The first ${formatSeconds(lead)} are black — video was enabled before the first selected camera or screen-share frames arrived.`
  }

  if (pipeline.segments.failed > 0) {
    return `${pipeline.segments.failed} of ${pipeline.segments.count} captured segments failed, so parts of this recording are missing.`
  }

  return null
}

function describeSegments(segments: VideoSegmentsSummary): string {
  if (segments.count === 0) return 'no video segments'
  return `${segments.count} video ${segments.count === 1 ? 'segment' : 'segments'}`
}

/**
 * Sized from what the render itself reports, so the surface is the right shape
 * before a single byte of MP4 has been fetched.
 *
 * This is the whole reason `width`/`height` are carried through the BFF. A
 * `<video>` with no intrinsic size is 300x150 until metadata lands, and on a
 * screen whose player is PINNED that swap does not just resize the video — it
 * moves the tab strip and the entire transcript under the reader's thumb.
 */
export function aspectRatioOf(render: VideoRenderStatus): number {
  const { width, height } = render
  if (typeof width === 'number' && typeof height === 'number' && width > 0 && height > 0) {
    return width / height
  }
  return DEFAULT_ASPECT
}

/**
 * Kept tolerant for cached responses produced before the state enum shipped.
 *
 * Deliberately permissive on the failure side and strict on the success side:
 * mistaking a failure for a wait strands the reader, while mistaking a wait for
 * a failure only costs one poll.
 */
function isFailedState(state: string | null | undefined): boolean {
  if (!state) return false
  const normalized = state.toLowerCase()
  return (
    normalized === 'failed' ||
    normalized === 'error' ||
    normalized === 'cancelled' ||
    normalized === 'canceled' ||
    normalized === 'abandoned'
  )
}

/** Upstream sends `""` as well as `null` for "no error" — seen on real programs. */
function hasText(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function formatSeconds(ms: number): string {
  const seconds = ms / 1000
  return seconds >= 10 ? `${Math.round(seconds)} seconds` : `${seconds.toFixed(1)} seconds`
}

/* -------------------------------------------------------------------------- */
/* polling                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Whether this answer can still change.
 *
 * The render event and the slow backstop both use this terminal test. It stops
 * all further reads the instant the answer cannot change.
 */
export function isVideoSettled(state: VideoState): boolean {
  return state.kind === 'ready' || state.kind === 'failed' || state.kind === 'absent'
}
